import { useLang } from "../i18n/LanguageContext";
import { GiBookmarklet, GiLockedChest, GiMoon, GiSpellBook } from "react-icons/gi";
import { DreamCodexPreview } from "./DreamCodexPreview";

const COPY = {
  pl: {
    eyebrow: "Księga Snów",
    title: "Miejsce, do którego wracają Twoje sny",
    subtitle:
      "Morfeusz nie kończy pracy na jednym odczycie. Sny mają tworzyć prywatną kronikę: zapisy, interpretacje, symbole, notatki i bezpieczną przestrzeń dla tego, co naprawdę osobiste.",
    cards: [
      {
        icon: GiSpellBook,
        title: "Zapis i interpretacja",
        desc: "Każdy sen może zostać zapisany razem z odczytem, datą, nastrojem i własną notatką.",
      },
      {
        icon: GiMoon,
        title: "Powracające symbole",
        desc: "Księga pozwoli zobaczyć, które obrazy wracają najczęściej i jak zmieniają się w czasie.",
      },
      {
        icon: GiLockedChest,
        title: "Sejf prywatności",
        desc: "Najbardziej osobiste sny wymagają jednego, spójnego mechanizmu odblokowania i ochrony.",
      },
    ],
    cta: "Księga Snów będzie sercem konta Morfeusza",
    note: "Na stronie głównej działa jeszcze demo. Zapis snów, konto, historia i Sejf będą częścią właściwej aplikacji.",
  },
  en: {
    eyebrow: "Dream Codex",
    title: "A place your dreams can return to",
    subtitle:
      "Morpheus does not end with a single reading. Dreams are meant to become a private chronicle: saved entries, interpretations, symbols, notes and a secure space for what is truly personal.",
    cards: [
      {
        icon: GiSpellBook,
        title: "Save and read",
        desc: "Each dream can be stored with its reading, date, mood and your own note.",
      },
      {
        icon: GiMoon,
        title: "Recurring symbols",
        desc: "The Codex will help reveal which images return most often and how they change over time.",
      },
      {
        icon: GiLockedChest,
        title: "Privacy vault",
        desc: "The most personal dreams need one consistent unlock and protection flow.",
      },
    ],
    cta: "The Dream Codex will become the heart of a Morpheus account",
    note: "The home page still runs in demo mode. Saved dreams, account, history and Vault will belong to the real application.",
  },
};

export const DreamsSection = () => {
  const { lang } = useLang();
  const copy = COPY[lang] ?? COPY.pl;

  return (
    <section
      id="dreams"
      className="bg-parchment-dark py-24 md:py-32 border-y border-ink/10"
      data-ui="home-dreams"
      data-testid="dreams-section"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10" data-ui="home-dreams-inner">
        <div className="md:grid md:grid-cols-12 md:gap-12 items-end">
          <div className="md:col-span-7">
            <span className="font-accent text-[11px] uppercase tracking-[0.3em] text-gold-muted">
              {copy.eyebrow}
            </span>
            <h2 className="font-heading italic text-4xl md:text-5xl text-ink mt-3 leading-tight">
              {copy.title}
            </h2>
          </div>
          <p className="md:col-span-5 font-body text-base text-ink-soft leading-relaxed mt-5 md:mt-0">
            {copy.subtitle}
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {copy.cards.map((card, index) => {
            const Icon = card.icon;
            return (
              <article
                key={card.title}
                className="bg-parchment p-8 md:p-10 group hover:bg-parchment-dark transition-colors duration-500"
                data-ui="home-dreams-card"
                data-testid={`dreams-card-${index}`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="font-accent text-[10px] uppercase tracking-[0.35em] text-gold-muted">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <Icon className="text-3xl text-ink-soft/60 group-hover:text-gold-muted transition-colors" />
                </div>
                <h3 className="font-heading italic text-3xl text-ink mt-8">
                  {card.title}
                </h3>
                <p className="font-body text-sm text-ink-soft mt-3 leading-relaxed">
                  {card.desc}
                </p>
              </article>
            );
          })}
        </div>

        <DreamCodexPreview />

        <div className="mt-12 ornate-frame bg-parchment p-1" data-ui="home-dreams-note">
          <div className="bg-parchment p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div className="flex items-start gap-4">
              <GiBookmarklet className="text-2xl text-gold-muted flex-shrink-0 mt-1" />
              <div>
                <div className="font-accent text-[11px] uppercase tracking-[0.25em] text-ink">
                  {copy.cta}
                </div>
                <p className="font-body italic text-sm text-ink-soft mt-2">
                  {copy.note}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
